'use client'

import { useState } from 'react'
import { useRouter, useParams, useSearchParams } from 'next/navigation'
import { Link } from '@/lib/i18n/navigation'
import { useTranslations } from 'next-intl'
import { ArrowLeft, Loader2, Check, Mail, Truck, Gavel } from 'lucide-react'
import { createClient } from '@/lib/supabase/client'
import { ListlogistLogo } from '@/components/brand/ListlogistLogo'
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher'
import { ThemeToggle } from '@/components/layout/ThemeToggle'
import { CompanyNameField } from '@/components/companies/CompanyNameField'

export default function SignupPage() {
  const t = useTranslations()
  const router = useRouter()
  const { locale } = useParams<{ locale: string }>()
  const searchParams = useSearchParams()
  const supabase = createClient()

  const inviteToken = searchParams.get('invite')
  const inviteEmail = searchParams.get('email')
  const isInvited = !!inviteToken

  // Роль из query (?role=importer или ?role=forwarder) с дефолтом 'forwarder'
  const roleFromUrl = searchParams.get('role')
  const initialRole: 'forwarder' | 'importer' = 
    roleFromUrl === 'importer' ? 'importer' : 'forwarder'

  const [businessType, setBusinessType] = useState<'forwarder' | 'importer'>(initialRole)

  // Роль внутри линии перевозок. Умолчание «экспедитор» — так работает
  // большинство, и оно же стоит в базе.
  const [companyRole, setCompanyRole] = useState<'forwarder' | 'carrier'>('forwarder')

  // Дополнение. Лендинг обещает «Выбрать при регистрации» — до сих пор
  // обещание не выполнялось: человек нажимал кнопку и попадал на форму,
  // где выбора не было.
  const [addonManagement, setAddonManagement] = useState(false)

  const [form, setForm] = useState({
    email: inviteEmail ?? '',
    password: '',
    full_name: '',
    company_name: '',
    phone: '',
    // Код из подсказки каталога. По нему (и по телефону) триггер регистрации
    // находит карточку, которую менеджер уже ведёт в «Фирмах», — вместо новой
    // пустой рядом. Правка названия руками код сбрасывает: он принадлежал
    // выбранной строке, а не тому, что вписано теперь.
    fiscal_code: '',
  })
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [needsConfirm, setNeedsConfirm] = useState(false)

  function set<K extends keyof typeof form>(key: K, value: string) {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    const { data, error } = await supabase.auth.signUp({
      email: form.email,
      password: form.password,
      options: {
        data: {
          full_name: form.full_name,
          company_name: isInvited ? null : form.company_name,
          // Телефон — и владельцу, и приглашённому (profiles.phone); код — только новой компании.
          phone: form.phone.trim(),
          fiscal_code: isInvited ? null : (form.fiscal_code || null),
          // Тип бизнеса передаётся в БД-триггер handle_new_user(),
          // который установит companies.business_type при создании компании.
          // Для приглашённых юзеров не передаём — компания уже существует.
          business_type: isInvited ? null : businessType,

          // ── РОЛЬ И ДОПОЛНЕНИЕ ────────────────────────────────────────────
          // Тем же путём, что и линия: триггер прочитает их при создании
          // компании. Приглашённым не передаём — компания уже существует, и
          // её настройки менять чужой регистрацией нельзя.
          //
          // Роль имеет смысл только у линии перевозок: у тендерной организатор
          // торгов и импортёр объявляют торги одинаково.
          company_role: isInvited || businessType !== 'forwarder' ? null : companyRole,
          addon_management: isInvited ? null : (addonManagement ? 'true' : 'false'),
          // The invitation token travels with the sign-up: handle_new_user accepts an invitation for an
          // unconfirmed address only when it carries this token (H16).
          invite_token: isInvited ? inviteToken : null,
        },
        // Куда вернуть после подтверждения email
        emailRedirectTo: `${origin}/${locale}/auth/confirm`,
      },
    })

    setLoading(false)
    if (error) {
      setError(error.message)
      return
    }

    // Если в Supabase включена email confirmation — session не вернётся,
    // user.email_confirmed_at будет null. Показываем экран "Проверьте почту".
    if (data.user && !data.session) {
      setNeedsConfirm(true)
      return
    }

    // Иначе (email confirmation отключена) — сразу в систему
    router.replace(isInvited ? `/${locale}/dashboard` : `/${locale}/onboarding/roles`)
    router.refresh()
  }

  return (
    <main className="min-h-screen bg-ink-50/40 dark:bg-ink-900 flex flex-col">
      <header className="px-6 py-4 flex items-center justify-between max-w-6xl w-full mx-auto">
        <div className="flex items-center gap-3">
          <Link href="/" className="flex items-center">
            <ListlogistLogo variant="full" size="md" />
          </Link>
          <LanguageSwitcher />
        </div>
        <div className="flex items-center gap-2">
          <Link
            href="/"
            className="text-sm text-ink-500 hover:text-ink-900 inline-flex items-center gap-1"
          >
            <ArrowLeft size={14} /> {t('auth.toHome')}
          </Link>
          <ThemeToggle />
        </div>
      </header>

      <div className="flex-1 flex items-start justify-center px-4 pt-6 pb-12">
        {needsConfirm ? (
          <div className="w-full max-w-sm">
            <div className="rounded-2xl border border-ink-300/60 bg-white p-7 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-50 text-ink-700 mb-3">
                <Mail size={22} />
              </div>
              <h1 className="text-lg font-semibold text-ink-900">{t('auth.signupPage.checkEmailTitle')}</h1>
              <p className="text-sm text-ink-500 mt-2">
                {t.rich('auth.signupPage.checkEmailBody', {
                  email: form.email,
                  b: (chunks) => <span className="text-ink-900 font-medium">{chunks}</span>,
                })}
              </p>
              <p className="text-xs text-ink-500 mt-3">
                {t('auth.signupPage.checkEmailSpam')}
              </p>
              <Link
                href={`/auth/login`}
                className="mt-5 inline-flex items-center justify-center gap-2 text-sm text-ink-900 font-medium hover:underline"
              >
                {t('auth.signupPage.backToLogin')}
              </Link>
            </div>
          </div>
        ) : (
        <div className="w-full max-w-4xl grid md:grid-cols-[1fr_320px] gap-6 items-start">

          {/* Левая колонка — форма */}
          <div className="rounded-2xl border border-ink-300/60 bg-white p-7">
            <h1 className="text-xl font-semibold text-ink-900">
              {isInvited ? t('auth.signupPage.inviteTitle') : t('auth.signupTitle')}
            </h1>
            {isInvited ? (
              <p className="text-sm text-ink-500 mt-1">
                {t('auth.signupPage.inviteSubtitle')}
              </p>
            ) : (
              <p className="text-sm text-ink-500 mt-1">
                {t('auth.signupPage.subtitle')}
              </p>
            )}

            <form onSubmit={onSubmit} className="mt-6 space-y-4">
              {/* Селектор роли — только для регистрации новой компании (не для приглашений) */}
              {!isInvited && (
                <div>
                  <label className="block text-xs font-medium text-ink-700 mb-2">
                    {t('auth.signupPage.businessQuestion')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setBusinessType('forwarder')}
                      className={
                        'flex items-start gap-2 rounded-lg border-2 px-3 py-2.5 text-left transition ' +
                        (businessType === 'forwarder'
                          ? 'border-primary-500 bg-primary-50/50 ring-2 ring-primary-100'
                          : 'border-ink-300/60 hover:border-ink-400')
                      }
                    >
                      <Truck size={16} className={businessType === 'forwarder' ? 'text-ink-700 mt-0.5 shrink-0' : 'text-ink-500 mt-0.5 shrink-0'} />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-ink-900">{t('auth.signupPage.forwarder')}</div>
                        <div className="text-[10px] text-ink-500 leading-tight">{t('auth.signupPage.forwarderDesc')}</div>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => setBusinessType('importer')}
                      className={
                        'flex items-start gap-2 rounded-lg border-2 px-3 py-2.5 text-left transition ' +
                        (businessType === 'importer'
                          ? 'border-amber-500 bg-amber-50/50 ring-2 ring-amber-100'
                          : 'border-ink-300/60 hover:border-ink-400')
                      }
                    >
                      <Gavel size={16} className={businessType === 'importer' ? 'text-amber-700 mt-0.5 shrink-0' : 'text-ink-500 mt-0.5 shrink-0'} />
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-ink-900">{t('auth.signupPage.importer')}</div>
                        <div className="text-[10px] text-ink-500 leading-tight">{t('auth.signupPage.importerDesc')}</div>
                      </div>
                    </button>
                  </div>

              {/* ══════════════════════════════════════════════════════════════
                  РОЛЬ И ДОПОЛНЕНИЕ.

                  Роль — только у линии перевозок: у тендерной организатор
                  торгов и импортёр объявляют торги и выбирают перевозчика
                  одинаково, и выбор из одного варианта обещал бы различие,
                  которого нет.

                  Дополнение — потому что лендинг УЖЕ обещает «Выбрать при
                  регистрации». Обещание было дано, а форма его не выполняла:
                  человек нажимал кнопку и попадал сюда, где выбора нет.

                  Оба вопроса НЕ обязательны: пропустил — работает как сегодня,
                  роль ставится «экспедитор», дополнение включается в тарифах.
                  ══════════════════════════════════════════════════════════════ */}
              {businessType === 'forwarder' && (
                <div className="mt-4">
                  <label className="mb-1.5 block text-[12px] font-medium text-ink-700">
                    {t('auth.signupPage.roleLabel')}
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {(['forwarder', 'carrier'] as const).map(r => (
                      <button
                        key={r}
                        type="button"
                        onClick={() => setCompanyRole(r)}
                        className={
                          'rounded-[10px] border p-2.5 text-left transition-colors ' +
                          (companyRole === r
                            ? 'border-accent-500 bg-accent-50'
                            : 'border-ink-200 hover:border-accent-500')
                        }
                      >
                        <div className="text-sm font-medium text-ink-900">
                          {t(`auth.signupPage.role_${r}`)}
                        </div>
                        {/* Формула — единственное, что различает роли.
                            Без неё выбор делается вслепую. */}
                        <div className="text-[10px] leading-tight text-ink-500">
                          {t(`auth.signupPage.role_${r}_calc`)}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4">
                <label className="flex cursor-pointer items-start gap-2.5 rounded-[10px] border border-ink-200 p-3">
                  <input
                    type="checkbox"
                    checked={addonManagement}
                    onChange={e => setAddonManagement(e.target.checked)}
                    className="mt-0.5 rounded"
                  />
                  <span>
                    <span className="block text-sm font-medium text-ink-900">
                      {t('auth.signupPage.addonManagement')}
                    </span>
                    <span className="block text-[10px] leading-tight text-ink-500">
                      {t('auth.signupPage.addonManagementDesc')}
                    </span>
                  </span>
                </label>
              </div>

                </div>
              )}

              <div>
                <label htmlFor="signup-full-name" className="block text-xs font-medium text-ink-700 mb-1">{t('auth.fullName')}</label>
                <input
                  id="signup-full-name"
                  required
                  value={form.full_name}
                  onChange={e => set('full_name', e.target.value)}
                  className="w-full rounded-md border border-ink-300 bg-white px-2.5 py-1.5 text-sm focus:border-ink-900 focus:outline-none focus:ring-1 focus:ring-ink-900/20"
                  placeholder={t('auth.signupPage.fullNamePlaceholder')}
                />
              </div>

              {!isInvited && (
                <div>
                  <label htmlFor="signup-company-name" className="block text-xs font-medium text-ink-700 mb-1">{t('auth.companyName')}</label>
                  {/* Подсказка из каталога: выбрал строку — название встало
                      полное и юридически верное.

                      Пока его вписывали руками, компания заводилась как
                      «Иригата» рядом с уже существующей «IRRIGATA-CROP SERVICE
                      SRL», и свести их было не по чему: код никто не вводил.

                      Показываем ТОЛЬКО каталог. Совпадение с фирмой из нашего
                      реестра человеку тут не место видеть — это чужая
                      клиентская база. */}
                  <CompanyNameField
                    id="signup-company-name"
                    required
                    value={form.company_name}
                    onChange={v => setForm(prev => ({ ...prev, company_name: v, fiscal_code: '' }))}
                    onPick={c => set('fiscal_code', c.code ?? '')}
                    className="w-full rounded-md border border-ink-300 bg-white px-2.5 py-1.5 text-sm focus:border-ink-900 focus:outline-none focus:ring-1 focus:ring-ink-900/20"
                    placeholder={t('auth.signupPage.companyPlaceholder')}
                  />
                  <p className="mt-1 text-[11px] text-ink-500">
                    {t('auth.signupPage.companyHint')}
                  </p>
                </div>
              )}

              <div>
                <label htmlFor="signup-phone" className="block text-xs font-medium text-ink-700 mb-1">{t('auth.phone')}</label>
                <input
                  id="signup-phone"
                  type="tel"
                  required
                  value={form.phone}
                  onChange={e => set('phone', e.target.value)}
                  className="w-full rounded-md border border-ink-300 bg-white px-2.5 py-1.5 text-sm focus:border-ink-900 focus:outline-none focus:ring-1 focus:ring-ink-900/20"
                  autoComplete="tel"
                  placeholder="+373 60 000 000"
                />
                {!isInvited && (
                  <p className="mt-1 text-[11px] text-ink-500">
                    {t('auth.signupPage.phoneHint')}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="signup-email" className="block text-xs font-medium text-ink-700 mb-1">{t('auth.email')}</label>
                <input
                  id="signup-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={e => set('email', e.target.value)}
                  className="w-full rounded-md border border-ink-300 bg-white px-2.5 py-1.5 text-sm focus:border-ink-900 focus:outline-none focus:ring-1 focus:ring-ink-900/20 disabled:bg-ink-50"
                  autoComplete="email"
                  disabled={isInvited}
                  placeholder="you@company.com"
                />
                {isInvited && (
                  <p className="mt-1 text-[11px] text-ink-500">
                    {t('auth.signupPage.inviteEmailHint')}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="signup-password" className="block text-xs font-medium text-ink-700 mb-1">{t('auth.password')}</label>
                <input
                  id="signup-password"
                  type="password"
                  required
                  minLength={8}
                  value={form.password}
                  onChange={e => set('password', e.target.value)}
                  className="w-full rounded-md border border-ink-300 bg-white px-2.5 py-1.5 text-sm focus:border-ink-900 focus:outline-none focus:ring-1 focus:ring-ink-900/20"
                  autoComplete="new-password"
                  placeholder={t('auth.passwordMin')}
                />
              </div>

              {error && (
                <div className="rounded-md border-hairline border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-ink-900 dark:bg-accent-500 text-white text-sm font-medium px-3 py-2 hover:bg-ink-800 dark:hover:bg-accent-600 transition disabled:opacity-50"
              >
                {loading && <Loader2 size={14} className="animate-spin" />}
                {loading ? t('common.loading') : (isInvited ? t('auth.signupPage.joinButton') : t('auth.signup'))}
              </button>

              <p className="text-[11px] text-ink-500 text-center leading-relaxed">
                {t('auth.signupPage.termsPrefix', { action: isInvited ? t('auth.signupPage.joinButton') : t('auth.signup') })}{' '}
                <Link href={`/terms`} className="text-ink-900 hover:underline" target="_blank">
                  {t('auth.signupPage.termsLink')}
                </Link>{' '}
                {t('auth.signupPage.termsAnd')}{' '}
                <Link href={`/privacy`} className="text-ink-900 hover:underline" target="_blank">
                  {t('auth.signupPage.privacyLink')}
                </Link>.
              </p>

              <p className="text-center text-sm text-ink-500">
                {t('auth.haveAccount')}{' '}
                <Link href={`/auth/login`} className="text-ink-900 font-medium hover:underline">
                  {t('auth.login')}
                </Link>
              </p>
            </form>
          </div>

          {/* Правая колонка — что вы получите */}
          <div className="hidden md:block">
            <div className="rounded-2xl border border-ink-300/60 bg-white p-5">
              <div className={
                'text-[11px] uppercase tracking-wider font-medium mb-3 ' +
                (businessType === 'forwarder' ? 'text-ink-700' : 'text-amber-700')
              }>
                {t('auth.signupPage.benefitsTitle')}
              </div>
              <ul className="space-y-2.5 text-sm">
                {(t.raw(businessType === 'forwarder'
                  ? 'auth.signupPage.benefitsForwarder'
                  : 'auth.signupPage.benefitsImporter') as string[]).map((line, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check size={13} className={
                      businessType === 'forwarder' ? 'text-ink-900 mt-0.5 shrink-0' : 'text-amber-600 mt-0.5 shrink-0'
                    } />
                    <span className="text-ink-700">{line}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 pt-4 border-t border-ink-300/40 text-[11px] text-ink-500">
                {t('auth.signupPage.trialNote')}
              </div>
            </div>
          </div>
        </div>
        )}
      </div>
    </main>
  )
}
